import { memo, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

import { LockIcon, LockOpenIcon } from '@/assets/icons';
import { StyledTooltip } from '@/helpers/styles';
import { LayerVisibilityMode } from '@/modules/common/types/layers';

type LayerActionValue = LayerVisibilityMode | 'lock';

type LayerActionsProps = {
  isLocked: boolean;
  visibilityMode?: LayerVisibilityMode;
  showAuto: boolean;
  showLock?: boolean;
  showVisibility?: boolean;
  disabled?: boolean;
  onToggleLock?: () => void;
  onVisibilityModeChange: (mode: LayerVisibilityMode) => void;
};

const getSelectedValues = ({
  isLocked,
  visibilityMode,
  showAuto,
  showLock,
  showVisibility,
}: {
  isLocked: boolean;
  visibilityMode?: LayerVisibilityMode;
  showAuto: boolean;
  showLock: boolean;
  showVisibility: boolean;
}): LayerActionValue[] => {
  const selectedValues: LayerActionValue[] = [];

  if (showAuto && visibilityMode === 'auto') {
    selectedValues.push('auto');
  }

  if (showVisibility && visibilityMode === 'force_show') {
    selectedValues.push('force_show');
  }

  if (showVisibility && visibilityMode === 'force_hide') {
    selectedValues.push('force_hide');
  }

  if (showLock && isLocked) {
    selectedValues.push('lock');
  }

  return selectedValues;
};

const LayerActionsComponent = ({
  isLocked,
  visibilityMode,
  showAuto,
  showLock = true,
  showVisibility = true,
  disabled = false,
  onToggleLock,
  onVisibilityModeChange,
}: LayerActionsProps) => {
  const { t } = useTranslation('interface');

  const handleAutoClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onVisibilityModeChange('auto');
  };

  const handleForceShowClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onVisibilityModeChange('force_show');
  };

  const handleForceHideClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onVisibilityModeChange('force_hide');
  };

  const handleLockClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onToggleLock?.();
  };

  const selectedValues = getSelectedValues({
    isLocked,
    visibilityMode,
    showAuto,
    showLock,
    showVisibility,
  });

  const autoTooltip = t('layers.actions.auto', 'Auto');
  const showTooltip = t('layers.actions.show', 'Show');
  const hideTooltip = t('layers.actions.hide', 'Hide');
  const lockTooltip = t('layers.actions.lock', 'Lock');
  const unlockTooltip = t('layers.actions.unlock', 'Unlock');

  const lockLabel = isLocked ? unlockTooltip : lockTooltip;

  return (
    <ToggleButtonGroup
      size='small'
      value={selectedValues}
      disabled={disabled}
      aria-label='Layer actions'
    >
      {showAuto && (
        <StyledTooltip title={autoTooltip} placement='top' enterDelay={1000}>
          <ToggleButton value='auto' onClick={handleAutoClick} aria-label={autoTooltip}>
            <AutoModeIcon fontSize='small' />
          </ToggleButton>
        </StyledTooltip>
      )}

      {showVisibility && (
        <StyledTooltip title={showTooltip} placement='top' enterDelay={1000}>
          <ToggleButton value='force_show' onClick={handleForceShowClick} aria-label={showTooltip}>
            <VisibilityIcon fontSize='small' />
          </ToggleButton>
        </StyledTooltip>
      )}

      {showVisibility && (
        <StyledTooltip title={hideTooltip} placement='top' enterDelay={1000}>
          <ToggleButton value='force_hide' onClick={handleForceHideClick} aria-label={hideTooltip}>
            <VisibilityOffIcon fontSize='small' />
          </ToggleButton>
        </StyledTooltip>
      )}

      {showLock && (
        <StyledTooltip title={lockLabel} placement='top' enterDelay={1000}>
          <ToggleButton value='lock' onClick={handleLockClick} aria-label={lockLabel}>
            {isLocked ? (
              <LockIcon data-testid='Lock icon' />
            ) : (
              <LockOpenIcon data-testid='Lock Open icon' />
            )}
          </ToggleButton>
        </StyledTooltip>
      )}
    </ToggleButtonGroup>
  );
};

export const LayerActions = memo(LayerActionsComponent);
